import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PeriodPicker } from "@/components/PeriodPicker";
import { presetRange, type DateRange, type Preset } from "@/lib/periods";
import { api } from "@/lib/api";
import type { UnansweredItem } from "@/lib/types";
import { fmt } from "@/lib/utils";

export function UnansweredPage() {
  const [preset, setPreset] = useState<Preset>("last30");
  const [custom, setCustom] = useState<DateRange>(presetRange("last30"));
  const range = preset === "custom" ? custom : presetRange(preset);

  const { data, isLoading } = useQuery({
    queryKey: ["unanswered", range],
    queryFn: () =>
      api
        .get<{ items: UnansweredItem[] }>("/unanswered", {
          from: range.from,
          to: range.to,
          limit: 100,
        })
        .then((r) => r.items),
  });

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">미답변 질문</h1>
        <p className="text-xs text-muted-foreground">
          빈도순 — KMS 보강 우선순위 후보
        </p>
      </header>

      <Card>
        <CardContent className="pt-4">
          <PeriodPicker
            preset={preset}
            custom={custom}
            onChange={(p, c) => {
              setPreset(p);
              setCustom(c);
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            미답변 빈도 TOP
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : (data ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground">데이터 없음</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-xs text-muted-foreground">
                <tr>
                  <th className="w-10 py-2 text-right">#</th>
                  <th className="px-3 py-2 text-left">질문</th>
                  <th className="w-20 py-2 text-right">빈도</th>
                  <th className="w-32 px-3 py-2 text-left">대표 query_id</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((it, i) => (
                  <tr key={it.last_query_id} className="border-b">
                    <td className="py-1.5 text-right text-muted-foreground">
                      {i + 1}
                    </td>
                    <td className="px-3 py-1.5">{it.question}</td>
                    <td className="py-1.5 text-right font-semibold text-red-600 tabular-nums">
                      {fmt(it.count)}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                      {it.last_query_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
